import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMusic } from '@fortawesome/free-solid-svg-icons';

function youtubeThumbUrl(videoId) {
  if (!videoId) return '';
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

function Thumb({ src, alt, className }) {
  return (
    <div className={className}>
      <div className="music-edit-info-thumb-fallback" aria-hidden="true">
        <FontAwesomeIcon icon={faMusic} />
      </div>
      {src ? <img src={src} alt={alt || ''} /> : null}
    </div>
  );
}

function CoverOption({
  label,
  src,
  selected,
  disabled,
  onSelect,
}) {
  return (
    <button
      type="button"
      className={`music-edit-info-cover-option${selected ? ' is-selected' : ''}`}
      onClick={onSelect}
      disabled={disabled || !src}
      aria-pressed={selected}
    >
      <Thumb
        src={src}
        alt={label}
        className="music-edit-info-thumb music-edit-info-thumb--lg"
      />
      <span className="music-edit-info-cover-label">{label}</span>
      {selected && <span className="music-edit-info-cover-selected">Selected</span>}
    </button>
  );
}

export default function EditTrackInfoModal({
  open,
  track,
  suggestion,
  saving = false,
  onCancel,
  onSave,
}) {
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [coverSource, setCoverSource] = useState('ai');

  useEffect(() => {
    if (!open) return;
    setTitle(suggestion?.title ?? track?.title ?? '');
    setArtist(suggestion?.artist ?? track?.artist ?? '');

    const aiArt = suggestion?.artwork || '';
    const ytArt = youtubeThumbUrl(track?.video_id);
    const current =
      track?.thumbnail_medium || track?.thumbnail_high || track?.thumbnail_default || '';

    if (aiArt && current && current === aiArt) {
      setCoverSource('ai');
    } else if (ytArt && current && (current === ytArt || current.includes('ytimg.com') || current.includes('ggpht.com'))) {
      setCoverSource('youtube');
    } else if (aiArt) {
      setCoverSource('ai');
    } else {
      setCoverSource('youtube');
    }
  }, [open, suggestion, track]);

  if (!open || !track) return null;

  const currentThumb =
    track.thumbnail_medium || track.thumbnail_high || track.thumbnail_default || '';
  const aiThumb = suggestion?.artwork || '';
  const youtubeThumb = youtubeThumbUrl(track.video_id);
  const selectedCover = coverSource === 'ai' ? (aiThumb || youtubeThumb) : (youtubeThumb || aiThumb);

  const handleSubmit = (e) => {
    e.preventDefault();
    const nextTitle = title.trim();
    if (!nextTitle || saving) return;
    onSave?.({
      video_id: track.video_id,
      title: nextTitle,
      artist: artist.trim(),
      cover: selectedCover || currentThumb,
    });
  };

  return (
    <>
      <div
        className="music-edit-info-backdrop"
        onClick={saving ? undefined : onCancel}
        aria-hidden="true"
      />
      <div
        className="music-edit-info-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Edit track info"
      >
        <div className="music-edit-info-header">
          <h2>Edit track info</h2>
          <p>Review the AI recommendation before saving.</p>
        </div>

        <div className="music-edit-info-current">
          <Thumb
            src={currentThumb}
            alt={track.title || 'Current track'}
            className="music-edit-info-thumb music-edit-info-thumb--sm"
          />
          <div className="music-edit-info-current-meta">
            <div className="music-edit-info-current-title">{track.title || 'Untitled'}</div>
            <div className="music-edit-info-current-artist">{track.artist || 'Unknown artist'}</div>
          </div>
        </div>

        <form className="music-edit-info-form" onSubmit={handleSubmit}>
          <div className="music-edit-info-covers" role="group" aria-label="Thumbnail">
            <CoverOption
              label="AI Recommended"
              src={aiThumb}
              selected={coverSource === 'ai'}
              disabled={saving}
              onSelect={() => setCoverSource('ai')}
            />
            <CoverOption
              label="YouTube"
              src={youtubeThumb}
              selected={coverSource === 'youtube'}
              disabled={saving}
              onSelect={() => setCoverSource('youtube')}
            />
          </div>

          <label className="music-edit-info-field">
            <span>Title</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={saving}
              autoFocus
            />
          </label>

          <label className="music-edit-info-field">
            <span>Artist</span>
            <input
              type="text"
              value={artist}
              onChange={(e) => setArtist(e.target.value)}
              disabled={saving}
            />
          </label>

          <div className="music-edit-info-actions">
            <button
              type="button"
              className="music-edit-info-btn music-edit-info-btn--ghost"
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="music-edit-info-btn music-edit-info-btn--primary"
              disabled={saving || !title.trim()}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
